import {
  mergeEventModuleOverrides,
  resolveAgendaFilters,
  resolveEventModules,
  type AgendaFilterOverride,
  type EventAppModules,
  type EventModuleKey,
  type EventModuleOverride,
} from '@conference/shared';

export function normalizeEventModules(
  modules?: EventAppModules | null,
): EventAppModules {
  const normalized: EventAppModules = {};

  for (const [key, override] of Object.entries(modules ?? {})) {
    const next: EventModuleOverride = {};
    if (typeof override?.enabled === 'boolean') next.enabled = override.enabled;
    if (typeof override?.label === 'string') next.label = override.label;
    if (typeof override?.order === 'number') next.order = override.order;
    if (Array.isArray(override?.filters)) {
      next.filters = override.filters.map((filter) => ({ ...filter }));
    }
    if (typeof override?.paper_ai === 'boolean') next.paper_ai = override.paper_ai;
    if (Array.isArray(override?.session_prep_types)) next.session_prep_types = [...override.session_prep_types];
    normalized[key] = next;
  }

  return normalized;
}

export function buildAgendaFilterDraft(
  sessionTypes: readonly string[],
  configured: readonly AgendaFilterOverride[] = [],
): AgendaFilterOverride[] {
  const types = [...sessionTypes, ...configured.map(({ type }) => type)];
  const visibleOverrides = configured.map((filter) => ({ ...filter, enabled: true }));
  return resolveAgendaFilters(types, { agenda: { filters: visibleOverrides } }).map((filter) => ({
    ...filter,
    enabled: configured.find(({ type }) => type.trim().toLowerCase() === filter.type)?.enabled ?? true,
  }));
}

export function updateEventModule(
  current: EventAppModules,
  key: EventModuleKey,
  edit: EventModuleOverride,
): EventAppModules {
  return mergeEventModuleOverrides(current, { [key]: edit });
}

export function resetEventModule(
  current: EventAppModules,
  key: EventModuleKey,
): EventAppModules {
  const next = Object.fromEntries(
    Object.entries(current).map(([moduleKey, value]) => [moduleKey, { ...value }]),
  );
  delete next[key];
  return next;
}

export function moveEventModule(
  current: EventAppModules,
  key: EventModuleKey,
  direction: 'up' | 'down',
): EventAppModules {
  const resolved = resolveEventModules(current);
  const index = resolved.findIndex((module) => module.key === key);
  const neighborIndex = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || neighborIndex < 0 || neighborIndex >= resolved.length) {
    return Object.fromEntries(
      Object.entries(current).map(([moduleKey, value]) => [moduleKey, { ...value }]),
    );
  }

  const reordered = [...resolved];
  [reordered[index], reordered[neighborIndex]] = [
    reordered[neighborIndex],
    reordered[index],
  ];
  const normalizedOrders = Object.fromEntries(
    reordered.map((module, position) => [module.key, { order: (position + 1) * 10 }]),
  ) as EventAppModules;
  return mergeEventModuleOverrides(current, normalizedOrders);
}
